import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo
} from "react";
import { useGetIndividualData } from "../hooks/useGetIndividualData";
import {
  IndividualState,
  UpdateIndividual
} from "../redux/individual/individualSlice";
import { useUpdateUser } from "../hooks/useUpdateUser";

interface IndividualContextType {
  individual: IndividualState | null;
  isLoading: boolean;
  error: Error | null;
  refetchIndividual: () => Promise<void>;
  updateIndividual: (data: UpdateIndividual) => Promise<void>;
}

const IndividualContext = createContext<IndividualContextType | undefined>(
  undefined
);

export const useIndividual = () => {
  const context = useContext(IndividualContext);
  if (!context) {
    throw new Error("useIndividual must be used within an IndividualProvider");
  }
  return context;
};

// Useful for components that may render outside an IndividualProvider.
export const useIndividualOptional = () => {
  return useContext(IndividualContext);
};

interface IndividualProviderProps {
  individualId: string;
  children: React.ReactNode;
}

export const IndividualProvider: React.FC<IndividualProviderProps> = ({
  individualId,
  children
}) => {
  const [error, setError] = useState<Error | null>(null);
  const [individualState, setIndividualState] =
    useState<IndividualState | null>(null);
  const {
    individualData: individualFromHook,
    isLoadingGetIndividualData: isLoading,
    onSubmitGetIndividualData: fetchIndividual
  } = useGetIndividualData();
  const { onSubmitUpdateUser } = useUpdateUser();

  // Keep local state in sync with hook data
  useEffect(() => {
    setIndividualState(individualFromHook);
  }, [individualFromHook]);

  const refetchIndividual = async () => {
    try {
      setError(null);
      await fetchIndividual(individualId);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch individual")
      );
    }
  };

  const updateIndividual = async (data: UpdateIndividual) => {
    if (!individualState) return;

    // Optimistic update - only update fields that are in the UpdateIndividual type
    setIndividualState((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        ...data
      } as IndividualState;
    });

    try {
      await onSubmitUpdateUser(individualId, data);
    } catch (err) {
      // Revert optimistic update on error
      setError(
        err instanceof Error ? err : new Error("Failed to update individual")
      );
      await refetchIndividual();
      throw err;
    }
  };

  useEffect(() => {
    refetchIndividual();
  }, [individualId]);

  const value = useMemo(
    () => ({
      individual: individualState,
      isLoading,
      error,
      refetchIndividual,
      updateIndividual
    }),
    [individualState, isLoading, error]
  );

  return (
    <IndividualContext.Provider value={value}>
      {children}
    </IndividualContext.Provider>
  );
};
